import { rejects } from "assert";
import Lottie from "lottie-react";
import React, { useRef, useState } from "react";
import { openBase64ImageInNewTab } from "../utils/helpers";
import ToastSwal from "./toastSwal";
import loadAnim from "../animations/load.json";

type ImageDraggerProps = {
  label?: string;
  description?: string;
  errorText?: string;
  initialPreview?: string;
  acceptedTypes?: string[];
  loadImage: boolean;
  maxSizeMB?: number;
  resize?: { maxWidth: number; maxHeight: number };
  onImageSelected?: (file: File, previewUrl: string) => void;
  className?: string;
  name: string;
  imgLoader?: string | any;
  error?: boolean;
};
export const ImageDrawerComponent = ({
  name,
  label = "Image/Banner",
  description = "Arrastre o seleccione una imagen",
  resize,
  loadImage = false,
  maxSizeMB,
  acceptedTypes,
  errorText = "Formato de imagen no valido",
  initialPreview,
  imgLoader,
  error,
  onImageSelected,
  className,
}: ImageDraggerProps) => {
  const [preview, setPreview] = useState<string>(initialPreview);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (initialPreview) {
      setPreview(initialPreview);
    }
  }, [initialPreview]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = () => {
        img.src = reader.result as string;
      };

      img.onload = () => {
        const canvas = document.createElement("canvas");
        let { width, height } = img;

        const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
        width *= ratio;
        height *= ratio;

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          if (!blob) return reject("Resize error");
          const reader2 = new FileReader();
          reader2.onloadend = () => resolve(reader2.result as string);
          reader2.readAsDataURL(blob);
        }, "image/jpeg");
      };

      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFile = async (file: File) => {
    if (acceptedTypes && !acceptedTypes.includes(file.type)) {
      ToastSwal({
        icon: "error",
        title: "Tipo de imagen no permitida",
        positions: "bottom-end",
        timer: 3000,
        timeProgressBar: false,
      });
      return;
    }

    const maxBytes = (maxSizeMB ?? 2) * 1024 * 1024;
    if (file.size > maxBytes) {
      ToastSwal({
        icon: "error",
        title: `La imagen no puede superar los ${maxSizeMB ?? 2}MB.`,
        positions: "bottom-end",
        timer: 3000,
        timeProgressBar: false,
      });
      return;
    }

    try {
      let previewUrl: string;

      if (resize) {
        previewUrl = await resizeImage(file, resize.maxWidth, resize.maxHeight);
      } else {
        previewUrl = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result as string);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
      }

      setPreview(previewUrl);
      if (onImageSelected) {
        onImageSelected(file, previewUrl);
      }
    } catch (err) {
      console.error(err.message);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };
  return (
    <div className={`flex flex-col items-center w-full ${className}`}>
      <p className="mb-2 text-base font-semibold text-neutral-800">{label}</p>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragActive(true);
        }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        className={`w-full flex  flex-col align-center max-w-md p-6 border-2 border-dashed rounded-md cursor-pointer text-center transition-all bg-[rgb(246,246,246,0.2)] duration-300 ${
          dragActive
            ? "border-blue-500 bg-blue-50"
            : error
            ? "border-red-500"
            : "border-gray-300"
        }`}
        onClick={() => inputRef.current?.click()}
      >
        {preview && !loadImage ? (
          <img
            src={preview}
            alt="preview"
            className="self-center object-contain h-40 transition-opacity shadow-sm w-fit cursor-zoom-in hover:opacity-80"
            onClick={() => openBase64ImageInNewTab(preview)}
          />
        ) : (
          <>
            <div className="flex justify-center mb-2">
              {imgLoader && !loadImage && !preview ? (
                <Lottie
                  animationData={imgLoader}
                  style={{
                    width: 120,
                    height: 120,
                  }}
                />
              ) : loadImage && preview ? (
                <Lottie
                  animationData={loadAnim}
                  style={{
                    width: 120,
                    height: 120,
                    zIndex: 9999,
                  }}
                />
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-10 h-10 text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16V4h10v12m1 4H6m8-4l-4-4m0 0l4-4m-4 4h12"
                  />
                </svg>
              )}
            </div>
            <p className="text-sm text-neutral-700">
              {!loadImage ? description : "Cargando imagen"}
            </p>
            <p className="mt-1 text-xs text-red-400">
              {!loadImage ? errorText : "Por favor espere..."}
            </p>
          </>
        )}
      </div>

      <input
        type="file"
        accept="image/*"
        id="fileInput"
        hidden
        ref={inputRef}
        name={name}
        onChange={handleChange}
      />
    </div>
  );
};
